import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Clock, CheckCircle2, XCircle, Copy, ShieldCheck } from "lucide-react";

export default function Payment() {
    const { t, config } = useApp();
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [unlocked, setUnlocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [txn, setTxn] = useState("");
    const [busy, setBusy] = useState(false);
    const price = config?.price || 299;
    const upiId = config?.upi_id;
    const upiConfigured = config?.upi_configured;

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/payment/status");
            setOrder(data.order);
            setUnlocked(data.paid_unlocked);
            return data;
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        (async () => {
            const d = await refresh();
            if (d && !d.paid_unlocked && (!d.order || d.order.status === "REJECTED")) {
                try { const { data } = await api.post("/payment/create"); setOrder(data.order); } catch (e) {}
            }
            setLoading(false);
        })();
    }, [refresh]);

    // poll while pending/submitted
    useEffect(() => {
        if (unlocked) return;
        if (order && ["PENDING", "SUBMITTED"].includes(order.status)) {
            const id = setInterval(refresh, 8000);
            return () => clearInterval(id);
        }
    }, [order, unlocked, refresh]);

    const submit = async () => {
        if (txn.trim().length < 3) { toast.error(t("common.required")); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/payment/submit", { order_id: order.order_id, method: "upi", txn_ref: txn.trim() });
            toast.success(data.owner_notified ? "Submitted. The owner has been notified." : "Submitted for verification.");
            await refresh();
        } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
        setBusy(false);
    };

    const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-forest/20 border-t-forest animate-spin" /></div>;

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14">
            {unlocked ? (
                <div className="card-soft p-9 text-center" data-testid="payment-approved">
                    <CheckCircle2 size={48} className="mx-auto text-forest" />
                    <h1 className="mt-4 text-3xl text-forest">{t("pay.approved")}</h1>
                    <button data-testid="go-assessment" onClick={() => nav("/assessment")} className="btn-primary mt-6 px-6 py-3 font-medium">{t("pay.go_assessment")}</button>
                </div>
            ) : order && order.status === "SUBMITTED" ? (
                <PendingCard t={t} order={order} price={price} />
            ) : order && order.status === "REJECTED" ? (
                <div className="card-soft p-9 text-center" data-testid="payment-rejected">
                    <XCircle size={44} className="mx-auto text-destructive" />
                    <h1 className="mt-4 text-2xl text-charcoal">{t("pay.rejected")}</h1>
                    {order.reject_reason && <p className="mt-2 text-sm text-charcoal/70">{order.reject_reason}</p>}
                    <button onClick={() => nav("/contact")} className="btn-primary mt-6 px-6 py-3 text-sm">{t("nav.contact")}</button>
                </div>
            ) : (
                <div>
                    <h1 className="text-3xl sm:text-4xl text-charcoal">{t("pay.title")}</h1>
                    <div className="mt-2 flex items-center gap-3 text-charcoal/70">
                        <span className="text-sm">{t("pay.amount")}: <strong className="text-forest">₹{price}</strong></span>
                        {order && <span className="text-xs text-charcoal/50">Order {order.order_id}</span>}
                    </div>

                    <div className="card-soft p-7 mt-6">
                        {upiConfigured ? (
                            <>
                                <p className="text-sm text-charcoal/75">{t("pay.instructions")}</p>
                                <div className="mt-4 rounded-xl bg-forest/5 border border-forest/10 p-4 space-y-3">
                                    <Row label={t("pay.upi_id")} value={upiId} onCopy={() => copy(upiId)} />
                                    {config?.upi_payee_name && <Row label={t("pay.payee")} value={config.upi_payee_name} />}
                                    <Row label={t("pay.amount")} value={`₹${price}`} />
                                </div>
                                <div className="mt-6">
                                    <label className="text-sm font-medium">{t("pay.txn_ref")}</label>
                                    <input data-testid="txn-ref-input" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder={t("pay.txn_placeholder")}
                                        className="mt-1 w-full rounded-xl border border-charcoal/15 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-forest/30" />
                                    <button data-testid="submit-payment-proof-button" onClick={submit} disabled={busy}
                                        className="btn-primary w-full mt-4 py-3.5 font-medium disabled:opacity-60">{busy ? t("common.saving") : t("pay.submit")}</button>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-4" data-testid="payment-not-configured">
                                <p className="text-charcoal/75">{t("pay.not_configured")}</p>
                                <button onClick={() => nav("/contact")} className="btn-primary mt-5 px-6 py-3 text-sm">{t("nav.contact")}</button>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 flex items-start gap-2 text-xs text-charcoal/55"><ShieldCheck size={15} className="mt-0.5 shrink-0" /> A transaction reference or screenshot is not proof of payment. The owner verifies the actual receipt before approving.</div>
                </div>
            )}
        </div>
    );
}

const PendingCard = ({ t, order, price }) => (
    <div className="card-soft p-9 text-center" data-testid="payment-pending">
        <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 flex items-center justify-center"><Clock size={28} className="text-gold" /></div>
        <h1 className="mt-4 text-2xl sm:text-3xl text-charcoal">{t("pay.pending_title")}</h1>
        <p className="mt-3 text-sm text-charcoal/75 leading-relaxed max-w-md mx-auto">{t("pay.pending_body")}</p>
        <div className="mt-6 inline-block text-left rounded-xl bg-forest/5 p-4 text-sm">
            <p className="text-charcoal/60 text-xs">Order ID</p><p className="font-medium">{order.order_id}</p>
            <p className="text-charcoal/60 text-xs mt-2">Transaction ref</p><p className="font-medium">{order.txn_ref}</p>
            <p className="text-charcoal/60 text-xs mt-2">{t("pay.amount")}</p><p className="font-medium">₹{price}</p>
            <p className="text-charcoal/60 text-xs mt-2">{t("pay.status")}</p><p className="font-medium text-gold">Awaiting verification</p>
        </div>
    </div>
);

const Row = ({ label, value, onCopy }) => (
    <div className="flex items-center justify-between gap-3">
        <div><p className="text-xs text-charcoal/55">{label}</p><p className="font-medium text-charcoal">{value}</p></div>
        {onCopy && <button onClick={onCopy} className="p-2 rounded-lg hover:bg-charcoal/5"><Copy size={15} /></button>}
    </div>
);
